import { X, FileText, Link as LinkIcon, FileJson, FileSpreadsheet, FileCode } from 'lucide-react';
import type { Attachment } from '@/api-types';
import { motion, AnimatePresence } from 'framer-motion';

export interface AttachmentPreviewProps {
	attachments: Attachment[];
	onRemove?: (id: string) => void;
	className?: string;
	compact?: boolean;
}

function getDocumentIcon(mimeType: string) {
	if (mimeType === 'application/json') return FileJson;
	if (mimeType === 'text/csv') return FileSpreadsheet;
	if (mimeType === 'text/html') return FileCode;
	return FileText;
}

function formatFileSize(bytes: number | undefined): string {
	if (bytes === undefined || bytes === null) return '';
	if (bytes < 1024) return `${bytes}B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function getDomain(url: string): string {
	try {
		return new URL(url).hostname;
	} catch {
		return url;
	}
}

/**
 * Unified preview component for all attachment types.
 * Renders image thumbnails, document file cards, and URL previews.
 */
export function AttachmentPreview({
	attachments,
	onRemove,
	className = '',
	compact = false,
}: AttachmentPreviewProps) {
	if (attachments.length === 0) return null;

	return (
		<div className={`flex flex-wrap gap-2 ${className}`}>
			<AnimatePresence mode="popLayout">
				{attachments.map((attachment) => (
					<motion.div
						key={attachment.id}
						initial={{ opacity: 0, scale: 0.8 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.8 }}
						transition={{ duration: 0.2 }}
					>
						{attachment.kind === 'image' ? (
							<ImagePreviewItem
								attachment={attachment}
								onRemove={onRemove}
								compact={compact}
							/>
						) : attachment.kind === 'document' ? (
							<DocumentPreviewItem
								attachment={attachment}
								onRemove={onRemove}
								compact={compact}
							/>
						) : attachment.kind === 'url' ? (
							<UrlPreviewItem
								attachment={attachment}
								onRemove={onRemove}
								compact={compact}
							/>
						) : attachment.kind === 'integration' ? (
							<IntegrationPreviewItem
								attachment={attachment}
								onRemove={onRemove}
								compact={compact}
							/>
						) : null}
					</motion.div>
				))}
			</AnimatePresence>
		</div>
	);
}

function ImagePreviewItem({ attachment, onRemove, compact }: {
	attachment: Extract<Attachment, { kind: 'image' }>;
	onRemove?: (id: string) => void;
	compact: boolean;
}) {
	return (
		<div className={`relative group ${compact ? 'w-12 h-12' : 'w-20 h-20'} rounded-lg overflow-hidden border border-border-primary bg-bg-3`}>
			<img
				src={`data:${attachment.mimeType};base64,${attachment.base64Data}`}
				alt={attachment.filename}
				className="w-full h-full object-cover"
			/>
			{onRemove && <RemoveButton id={attachment.id} onRemove={onRemove} />}
			{!compact && (
				<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-bg-1/90 to-transparent p-1">
					<p className="text-[10px] text-text-secondary truncate">{attachment.filename}</p>
				</div>
			)}
		</div>
	);
}

function DocumentPreviewItem({ attachment, onRemove, compact }: {
	attachment: Extract<Attachment, { kind: 'document' }>;
	onRemove?: (id: string) => void;
	compact: boolean;
}) {
	const Icon = getDocumentIcon(attachment.mimeType);
	return (
		<div className={`relative group flex items-center gap-2 ${compact ? 'px-2 py-1' : 'px-3 py-2'} rounded-lg border border-border-primary bg-bg-3`}>
			<Icon className={compact ? 'size-3.5' : 'size-4'} strokeWidth={1.5} />
			<div className="min-w-0">
				<p className={`${compact ? 'text-[10px]' : 'text-xs'} text-text-primary truncate max-w-[120px]`}>
					{attachment.filename}
				</p>
				{!compact && attachment.size && (
					<p className="text-[10px] text-text-tertiary">{formatFileSize(attachment.size)}</p>
				)}
			</div>
			{onRemove && <RemoveButton id={attachment.id} onRemove={onRemove} />}
		</div>
	);
}

function UrlPreviewItem({ attachment, onRemove, compact }: {
	attachment: Extract<Attachment, { kind: 'url' }>;
	onRemove?: (id: string) => void;
	compact: boolean;
}) {
	return (
		<div className={`relative group flex items-center gap-2 ${compact ? 'px-2 py-1' : 'px-3 py-2'} rounded-lg border border-border-primary bg-bg-3`}>
			<LinkIcon className={compact ? 'size-3.5' : 'size-4'} strokeWidth={1.5} />
			<div className="min-w-0">
				<p className={`${compact ? 'text-[10px]' : 'text-xs'} text-text-primary truncate max-w-[160px]`}>
					{attachment.title || getDomain(attachment.url)}
				</p>
				{!compact && (
					<p className="text-[10px] text-text-tertiary truncate max-w-[160px]">{getDomain(attachment.url)}</p>
				)}
			</div>
			{onRemove && <RemoveButton id={attachment.id} onRemove={onRemove} />}
		</div>
	);
}

function IntegrationPreviewItem({ attachment, onRemove, compact }: {
	attachment: Extract<Attachment, { kind: 'integration' }>;
	onRemove?: (id: string) => void;
	compact: boolean;
}) {
	return (
		<div className={`relative group flex items-center gap-2 ${compact ? 'px-2 py-1' : 'px-3 py-2'} rounded-lg border border-border-primary bg-bg-3`}>
			<FileText className={compact ? 'size-3.5' : 'size-4'} strokeWidth={1.5} />
			<div className="min-w-0">
				<p className={`${compact ? 'text-[10px]' : 'text-xs'} text-text-primary truncate max-w-[160px]`}>
					{attachment.title}
				</p>
				{!compact && (
					<p className="text-[10px] text-text-tertiary capitalize">{attachment.provider.replace('_', ' ')}</p>
				)}
			</div>
			{onRemove && <RemoveButton id={attachment.id} onRemove={onRemove} />}
		</div>
	);
}

function RemoveButton({ id, onRemove }: { id: string; onRemove: (id: string) => void }) {
	return (
		<button
			type="button"
			onClick={() => onRemove(id)}
			className="absolute top-1 right-1 p-0.5 rounded-full bg-bg-1/90 hover:bg-bg-1 text-text-primary opacity-0 group-hover:opacity-100 transition-opacity"
			aria-label="Remove attachment"
		>
			<X className="size-3" />
		</button>
	);
}
